import React, {FC} from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import ArrowDown from '@pixel/assets/icons/SortDown'
import ErrorBoundary from '@pixel/core/ErrorBoundary'
import TextField from '@pixel/core/TextField'
import { styled } from '@pixel/core/styles'
import ConditionListSection from './ConditionListSection'
import LocalizedTypography from '../../components/LocalizedTypography'
import TriggerSection from './TriggerSection'
import UserAutoComplete from '../../components/UserAutoComplete'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import { renderLocalizedMenuItems } from '../../components/LocalizedMenuItem'
import { useMultipleLocalizedText } from '../../contexts/Localization/localizationHooks'
import ActionListSection from "./ActionListSection";
import {AutomationSearchResult, DefaultAutomationType} from "../types/Automations";
import SecondaryAutomationSection from "./SecondaryAutomationSection";
import HelpIcon from '@pixel/assets/icons/Help';
import { Tooltip } from '@mui/material';

const FormSectionContainer = styled('div')(({ theme }) => ({
    backgroundColor: 'white',
    borderRadius: '8px',
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}))

const AutomationFormContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
})

const HelpContainer = styled('div')(({ theme }) => ({
    color: '#5B6971',
    float: 'right',
    marginBottom: theme.spacing(-3),
    marginTop: '2px',
    position: 'relative',
    zIndex: 111
}))

const FormHeaderSection = styled('div')(({ theme }) => ({
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingBottom: theme.spacing(3),
}))

const FormBodySection = styled('div')({
    backgroundColor: '#e4edf3',
    flexGrow: 1,
})

const ArrowDisplay = styled(ArrowDown)(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(3),
}))

const AUTOMATION_TYPES = [
    { value: 'AUTOMATED', text: LocalizedText.AUTOMATION_TYPE_AUTO },
    { value: 'ON_DEMAND', text: LocalizedText.AUTOMATION_TYPE_ONDEMAND },
]

const secondaryAutomationEnabled = window.secondaryAutomationEngineEnabled;

interface AutomationFormProps {
    selectedAutomation: AutomationSearchResult
    errors: any
    setErrors: any
}
const AutomationForm: FC<AutomationFormProps> = ({
   selectedAutomation,
   errors,
   setErrors
}) => {
    const {
        AUTOMATION_FORM_NAME,
        AUTOMATION_FORM_TYPE,
        AUTOMATION_TYPE_TOOLTIP
    } = useMultipleLocalizedText()

    const { control } = useFormContext()

    const automationType = useWatch({ name: 'automationType', control })

    return (
        <AutomationFormContainer>
            <FormHeaderSection>
                <Controller
                    control={control}
                    name="name"
                    rules={{ maxLength: 100, required: true, }}
                    render={({ field, fieldState, }) => (
                        <TextField
                            id='automation-form-name-field'
                            required
                            label={AUTOMATION_FORM_NAME}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            error={fieldState.invalid}
                            errorText={fieldState?.error?.message || ''}
                            fullWidth
                            sx={{ marginBottom: 1 }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="automationType"
                    rules={{ required: true }}
                    render={({ field, fieldState, }) => (
                        <>
                            <Tooltip title={AUTOMATION_TYPE_TOOLTIP} placement="top">
                                 <HelpContainer>
                                      <HelpIcon sx={{ fontSize: '15px'}} />
                                </HelpContainer>
                            </Tooltip>
                            <TextField
                                id='automation-form-type-field'
                                select
                                required
                                label={AUTOMATION_FORM_TYPE}
                                value={field.value}
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                                error={fieldState.invalid}
                                errorText={fieldState?.error?.message || ''}
                                fullWidth
                            >
                                { AUTOMATION_TYPES.map(renderLocalizedMenuItems) }
                            </TextField>
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="status"
                    rules={{ required: true }}
                    defaultValue={"ACTIVE"}
                    render={() => (
                        <TextField
                            value={"ACTIVE"}
                            hidden={true}
                        >
                        </TextField>
                    )}
                />
                <ServiceAccountInput/>
            </FormHeaderSection>
            <FormBodySection>
                {automationType === DefaultAutomationType &&
                    <>
                        <FormSectionContainer>
                            <LocalizedTypography
                                variant="h3"
                                text={LocalizedText.AUTOMATION_SECTION_WHENTHISHAPPENS}
                            />
                            <ErrorBoundary>
                                <TriggerSection />
                            </ErrorBoundary>
                        </FormSectionContainer>

                        <ArrowDisplay />
                    </>
                }

                <FormSectionContainer>
                    <LocalizedTypography
                        variant="h3"
                        text={LocalizedText.AUTOMATION_SECTION_CONTINUEIF}
                    />
                    <ErrorBoundary>
                        <ConditionListSection selectedAutomation={selectedAutomation} errors={errors} setErrors={setErrors}/>
                    </ErrorBoundary>
                </FormSectionContainer>

                <ArrowDisplay />

                <FormSectionContainer sx={{ marginBottom: secondaryAutomationEnabled ? 0  : 3}}>
                    <LocalizedTypography
                        variant="h3"
                        text={LocalizedText.AUTOMATION_SECTION_THENDOTHIS}
                    />
                    <ErrorBoundary>
                        <ActionListSection />
                    </ErrorBoundary>
                </FormSectionContainer>

                { secondaryAutomationEnabled ?
                    <>
                        <ArrowDisplay />

                        <FormSectionContainer sx={{ marginBottom: 3 }}>
                            <LocalizedTypography
                                variant="h3"
                                text={LocalizedText.AUTOMATION_SECTION_RUN_SECONDARY}
                            />
                                <ErrorBoundary>
                                    <SecondaryAutomationSection selectedAutomation={selectedAutomation} />
                                </ErrorBoundary>
                        </FormSectionContainer>
                    </> : null
                }
            </FormBodySection>
        </AutomationFormContainer>
    )
}

const ServiceAccountInput: FC = ({
}) => {
    const { control ,setValue} = useFormContext()

    const {
        AUTOMATION_USER_TOOLTIP,
    } = useMultipleLocalizedText()

    const automationType = useWatch({ name: 'automationType', control })
    const actionUser = useWatch({ name: 'actionUserEId', control })

    if (automationType === DefaultAutomationType) {
        return (
            <>
                <Controller
                    control={control}
                    name="actionUser"
                    rules={{ validate: (value) => value }}
                    render={({ field }) => (
                        <div>
                            <Tooltip title={AUTOMATION_USER_TOOLTIP} placement="top">
                                 <HelpContainer >
                                      <HelpIcon sx={{ fontSize: '15px'}}/>
                                </HelpContainer>
                            </Tooltip>
                            <UserAutoComplete
                                id='automation-form-user-field'
                                optional={false}
                                error={actionUser === "none"}
                                value={field.value}
                                labelLocale={LocalizedText.AUTOMATION_FORM_ACCOUNT}
                                onChange={(e, newVal) => {
                                    field.onChange(newVal);
                                    setValue('actionUserEId', newVal.EId);
                                }}
                            />
                        </div>
                    )}
                    shouldUnregister
                />
                <Controller
                    control={control}
                    name="actionUserEId"
                    render={({ field, }) => (
                        <TextField
                            {...field}
                            hidden={true}
                        >
                        </TextField>
                    )}
                />
            </>
        )
    }

    return null
}

export default AutomationForm
